import './Username.css';
import { useState } from 'react';
import {
    Button, CircularProgress, IconButton, Modal,
    Skeleton, TextField, Tooltip, Typography
} from '@mui/material';
import { Box } from '@mui/system';
import regex from './usernameRegex';
import { useTranslation } from 'react-i18next';
import { FaRegCopy } from 'react-icons/fa6';
import apiClient from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useProfile } from '../../context/ProfileContext';

const Username = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { user: { username }, updateUsername } = useAuth() as any;
    const { loading } = useProfile();

    const [modalOpen, setModalOpen] = useState(false);
    const [userNameValue, setUserNameValue] = useState('');
    const [errorUsername, setErrorUsername] = useState(false);
    const [errorTextUsername, setErrorTextUsername] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [disabledSave, setDisabledSave] = useState(true);

    const [copytext, setCopyText] = useState('');
    const [openCopy, setOpenCopy] = useState(false);

    const handleOpenModal = () => {
        setUserNameValue(username ?? '');
        setErrorUsername(false);
        setErrorTextUsername('');
        setDisabledSave(true);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (verifying) return;
        setModalOpen(false);
    };

    const handleChangeUser = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[̀-ͯ]|[^0-9a-z]|[#\s]/g, '');
        setUserNameValue(value);
        if (value.length > 30) {
            setErrorUsername(true);
            setDisabledSave(true);
            setErrorTextUsername(t('profile.username.maximum'));
        } else if (value.length <= 4) {
            setErrorUsername(true);
            setDisabledSave(true);
            setErrorTextUsername(t('profile.username.minimum'));
        } else if (!/[a-z]/.test(value)) {
            setErrorUsername(true);
            setDisabledSave(true);
            setErrorTextUsername(t('profile.username.letter'));
        } else if (value.match(regex)) {
            setErrorUsername(true);
            setDisabledSave(true);
            setErrorTextUsername(t('profile.username.allowed'));
        } else {
            setErrorUsername(false);
            setDisabledSave(false);
            setErrorTextUsername('');
        }
    };

    const handleVerifySave = async () => {
        if (username != null && username.toLowerCase() === userNameValue.toLowerCase()) {
            showToast('info', t('profile.username.info'));
            return;
        }
        setVerifying(true);
        setDisabledSave(true);
        let saved = false;
        apiClient.put('/user/update', { newUsername: userNameValue.toLowerCase() })
            .then((response) => {
                const data = response.data;
                if (response.status === 200 && data.status === 1) {
                    saved = true;
                    updateUsername(userNameValue.toLowerCase());
                    showToast('success', t('profile.username.success'));
                    setModalOpen(false);
                }
                if (response.status === 200 && data.status === -1) {
                    showToast('error', t('profile.username.error.username-exist'));
                }
                if (response.status === 200 && data.status === -2) {
                    showToast('error', t('profile.username.error.unexpected'));
                }
            })
            .catch(() => {
                showToast('error', t('profile.username.error.unexpected'));
            })
            .finally(() => {
                setVerifying(false);
                if (!saved) setDisabledSave(false);
            });
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`https://linkerto.me/${username}`);
        setCopyText(t('profile.username.copied'));
    };

    return (
        <>
            <div className="signature-username">
                {loading ? (
                    <>
                        <Skeleton variant="text" width={180} height={22} />
                        <Button size="small" variant="outlined" disabled>
                            <div className="btn-text-container"><Skeleton width={60} height={13} /></div>
                        </Button>
                    </>
                ) : (
                    <>
                        <div className="signature-url-block">
                            <Typography component="span" className="signature-url">
                                <span className="signature-url-domain">linkerto.me/</span>
                                <span className="signature-url-user">{username ?? '—'}</span>
                            </Typography>
                            <Tooltip
                                open={openCopy}
                                onClose={() => setOpenCopy(false)}
                                onOpen={() => { setOpenCopy(true); setCopyText(t('profile.username.copy')); }}
                                disableFocusListener
                                placement="top"
                                arrow
                                title={copytext}
                            >
                                <IconButton
                                    onClick={handleCopyLink}
                                    size="small"
                                    className="signature-copy-btn"
                                    disabled={!username || !window.isSecureContext}
                                    aria-label={t('profile.username.copy')}
                                >
                                    <FaRegCopy size={13} />
                                </IconButton>
                            </Tooltip>
                        </div>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleOpenModal}
                        >
                            {t('profile.username.edit')}
                        </Button>
                    </>
                )}
            </div>

            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box
                    className="username-modal-box"
                    sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 24,
                    }}
                >
                    <Typography variant="h6" className="username-modal-title" color="primary">
                        {t('profile.username.modal.title')}
                    </Typography>

                    <div className="username-modal-input-row">
                        <Typography variant="body1" className="username-modal-prefix" sx={{ color: 'text.secondary' }}>
                            linkerto.me/
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            variant="standard"
                            placeholder="myusername"
                            value={userNameValue}
                            onChange={handleChangeUser}
                            error={errorUsername}
                            helperText={errorUsername ? errorTextUsername : t('profile.username.hint')}
                            disabled={verifying}
                        />
                    </div>

                    <div className="username-modal-actions">
                        <Button size="small" variant="outlined" onClick={handleCloseModal} disabled={verifying}>
                            {t('profile.username.cancel-edit')}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={disabledSave || verifying}
                            onClick={handleVerifySave}
                            startIcon={verifying ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {t('profile.username.verify-save')}
                        </Button>
                    </div>
                </Box>
            </Modal>
        </>
    );
};

export default Username;
